import classNames from 'classnames';
import type { ReactNode } from 'react';
import { Link } from 'react-router';

import { FeatureFlag, PagePaths } from '@revelio/core';
import { Tab } from '@revelio/data-access';

import styles from './HomepageCards.module.css';
import {
  DashboardsIcon,
  DataIcon,
  DiscoveryIcon,
  ReportsIcon,
  ToolsIcon,
} from './HomepageIcons';

type CardLink = {
  label: string;
  route: string;
  requiredTabs?: Tab[];
  requiredFeatureFlag?: FeatureFlag;
};

type PageCardProps = {
  id: string;
  icon?: ReactNode;
  title: string;
  subtitle: string;
  links?: CardLink[];
};

const dashboardRoutes = [
  {
    tab: Tab.Overview,
    routes: {
      Company: PagePaths.COMPANY_COMPOSITIONS,
      Industry: PagePaths.INDUSTRY_COMPOSITIONS,
      Geography: PagePaths.GEOGRAPHY_COMPOSITIONS,
      Role: PagePaths.ROLE_COMPOSITIONS,
    },
  },
  {
    tab: Tab.Transition,
    routes: {
      Company: PagePaths.COMPANY_TRANSITIONS,
      Industry: PagePaths.INDUSTRY_TRANSITIONS,
      Geography: PagePaths.GEOGRAPHY_TRANSITIONS,
      Role: PagePaths.ROLE_TRANSITIONS,
    },
  },
  {
    tab: Tab.Posting,
    routes: {
      Company: PagePaths.COMPANY_POSTINGS,
      Industry: PagePaths.INDUSTRY_POSTINGS,
      Geography: PagePaths.GEOGRAPHY_POSTINGS,
      Role: PagePaths.ROLE_POSTINGS,
    },
  },
  {
    tab: Tab.Compensation,
    routes: {
      Company: PagePaths.COMPANY_PAY,
      Industry: PagePaths.INDUSTRY_PAY,
      Geography: PagePaths.GEOGRAPHY_PAY,
      Role: PagePaths.ROLE_PAY,
    },
  },
  {
    tab: Tab.Sentiment,
    routes: {
      Company: PagePaths.COMPANY_SENTIMENT,
      Industry: PagePaths.INDUSTRY_SENTIMENT,
      Geography: PagePaths.GEOGRAPHY_SENTIMENT,
      Role: PagePaths.ROLE_SENTIMENT,
    },
  },
] as const;

// Entities with pages for only some of the tabs above: each links to the first
// of its own pages the user's tabs allow.
const partialDashboardEntities = [
  {
    label: 'Skill',
    routes: [
      { tab: Tab.Overview, route: PagePaths.SKILL_COMPOSITIONS },
      { tab: Tab.Posting, route: PagePaths.SKILL_POSTINGS },
      { tab: Tab.Compensation, route: PagePaths.SKILL_PAY },
    ],
  },
  {
    label: 'Activity',
    routes: [
      { tab: Tab.Overview, route: PagePaths.ACTIVITY_COMPOSITIONS },
      { tab: Tab.Posting, route: PagePaths.ACTIVITY_POSTINGS },
      { tab: Tab.Compensation, route: PagePaths.ACTIVITY_PAY },
    ],
  },
] as const;

const dashboardCardBase: Omit<PageCardProps, 'links'> = {
  id: 'dashboards',
  icon: <DashboardsIcon />,
  title: 'Dashboards',
  subtitle: 'Real-time workforce intelligence data',
};

const getDashboardsCard = (userTabs: Tab[]): PageCardProps => {
  const routeSet = dashboardRoutes.find(({ tab }) => userTabs.includes(tab));
  const standardLinks = routeSet
    ? Object.entries(routeSet.routes).map(([label, route]) => ({
        label,
        route: `/${route}`,
      }))
    : [];

  const partialLinks = partialDashboardEntities.flatMap(({ label, routes }) => {
    const route = routes.find(({ tab }) => userTabs.includes(tab))?.route;
    return route ? [{ label, route: `/${route}` }] : [];
  });

  return { ...dashboardCardBase, links: [...standardLinks, ...partialLinks] };
};

const discoveryCard: PageCardProps = {
  id: 'discovery',
  icon: <DiscoveryIcon />,
  title: 'Discovery',
  subtitle: 'Find people, companies, & markets',
  links: [
    {
      label: 'Individual',
      route: `/${PagePaths.TALENT_DISCOVERY}`,
      requiredTabs: [Tab.TalentDiscovery],
    },
    {
      label: 'Postings',
      route: `/${PagePaths.POSTING_DISCOVERY}`,
      requiredTabs: [Tab.TalentDiscovery],
    },
    {
      label: 'Company',
      route: `/${PagePaths.COMPANY_SCREENER}`,
      requiredTabs: [Tab.Screener],
    },
    {
      label: 'Geography',
      route: `/${PagePaths.GEOGRAPHY_SCREENER}`,
      requiredTabs: [Tab.Screener],
    },
    {
      label: 'Role',
      route: `/${PagePaths.ROLE_SCREENER}`,
      requiredTabs: [Tab.Screener],
    },
  ],
};

const toolsCard: PageCardProps = {
  id: 'tools',
  icon: <ToolsIcon />,
  title: 'Tools',
  subtitle: 'Decision-making tools & workflows',
  links: [
    {
      label: 'Data Enrichment',
      route: `/${PagePaths.DELIVERABLES}/${PagePaths.DATA_ENRICHMENT}`,
      requiredTabs: [Tab.DataEnrichment],
    },
    {
      label: 'Resume Enrichment',
      route: `/${PagePaths.RESUME_ENRICHMENT}`,
      requiredTabs: [Tab.ResumeParsing],
    },
    {
      label: 'Economic Impact',
      route: '/economic-impact',
      requiredFeatureFlag: FeatureFlag.EconomicImpactNewLayoutApp,
    },
  ],
};

const reportsCard: PageCardProps = {
  id: 'reports',
  icon: <ReportsIcon />,
  title: 'Reports',
  subtitle: 'Workforce insights & benchmarks',
  links: [
    {
      label: 'Report Builder',
      route: `/${PagePaths.REPORT_BUILDER}`,
      requiredTabs: [Tab.Reports],
    },
    {
      label: 'Reports Status',
      route: `/${PagePaths.REPORTS}`,
      requiredTabs: [Tab.Reports],
    },
  ],
};

const dataCard: PageCardProps = {
  id: 'data',
  icon: <DataIcon />,
  title: 'Data',
  subtitle: 'Generate a custom workforce dataset',
  links: [
    {
      label: 'Data Builder',
      route: `/${PagePaths.DATA_BUILDER}`,
      requiredTabs: [Tab.Deliverables],
    },
    {
      label: 'Delivery Status',
      route: `/${PagePaths.DATA_BUILDER_STATUS}`,
      requiredTabs: [Tab.Deliverables],
    },
    {
      label: 'Data Dictionary',
      route: `/${PagePaths.DATA_DICTIONARY}`,
      requiredTabs: [Tab.DataDictionary],
    },
  ],
};

// The default subtitle describes Data Builder, but the card also renders for a
// plan whose only Data link is the Data Dictionary.
const getDataCard = (userTabs: Tab[]): PageCardProps =>
  userTabs.includes(Tab.Deliverables)
    ? dataCard
    : { ...dataCard, subtitle: 'Metric definitions & methodology' };

const PageCard = ({ id, title, subtitle, icon, links }: PageCardProps) => {
  const titleId = `homepage-card-${id}-title`;

  return (
    <section className={styles.card} aria-labelledby={titleId}>
      <div aria-hidden="true">{icon}</div>
      <h2 id={titleId} className={styles.title}>
        {title}
      </h2>
      <p className={styles.subtitle}>{subtitle}</p>
      <div className={styles.links}>
        {links?.map((link) => (
          <Link key={link.route} to={link.route} className={styles.link}>
            {link.label}
          </Link>
        ))}
      </div>
    </section>
  );
};

export const getUserCards = (
  userTabs: Tab[],
  enabledFeatureFlags: FeatureFlag[] = []
): PageCardProps[] =>
  [
    getDashboardsCard(userTabs),
    discoveryCard,
    toolsCard,
    reportsCard,
    getDataCard(userTabs),
  ].flatMap((card) => {
    const links = card.links?.filter(
      ({ requiredTabs, requiredFeatureFlag }) =>
        (!requiredTabs || requiredTabs.some((tab) => userTabs.includes(tab))) &&
        (!requiredFeatureFlag ||
          enabledFeatureFlags.includes(requiredFeatureFlag))
    );

    return links?.length ? [{ ...card, links }] : [];
  });

export const HomepageCards = ({
  cards,
  className,
}: {
  cards: PageCardProps[];
  className?: string;
}) => {
  return (
    <div className={classNames(styles.cardsContainer, className)}>
      {cards.map((card) => (
        <PageCard key={card.id} {...card} />
      ))}
    </div>
  );
};
